import React from 'react';

import {
  HousekeepingCreationMode,
  HousekeepingOrderCardCompactProps,
  HousekeepingOrderStatus
} from './types';

const statusConfig: Record<
  HousekeepingOrderStatus,
  {
    label: string;
    primaryAction: string;
    accent: string;
    accentText: string;
    soft: string;
    border: string;
    text: string;
    cardGlow: string;
  }
> = {
  new: {
    label: 'جديد',
    primaryAction: 'بدء التنظيف',
    accent: 'bg-amber-500',
    accentText: 'text-amber-700',
    soft: 'bg-amber-50',
    border: 'border-amber-200',
    text: 'text-amber-800',
    cardGlow: 'shadow-amber-100'
  },
  in_progress: {
    label: 'قيد التنفيذ',
    primaryAction: 'اضغط إنهاء التنظيف',
    accent: 'bg-sky-500',
    accentText: 'text-sky-700',
    soft: 'bg-sky-50',
    border: 'border-sky-200',
    text: 'text-sky-800',
    cardGlow: 'shadow-sky-100'
  },
  done: {
    label: 'تم التنظيف',
    primaryAction: 'تم التنظيف',

    accent: 'bg-emerald-500',
    accentText: 'text-emerald-700',
    soft: 'bg-emerald-50',
    border: 'border-emerald-200',
    text: 'text-emerald-800',
    cardGlow: 'shadow-emerald-100'
  }
};

const formatHousekeepingDate = (
  orderDate?: string | number | Date,
  creationMode: HousekeepingCreationMode = 'auto'
) => {
  const dateValue = orderDate ? new Date(orderDate) : new Date();

  if (Number.isNaN(dateValue.getTime())) {
    return {
      value: '-'
    };
  }

  const formatter = new Intl.DateTimeFormat('ar-EG', {
    dateStyle: 'medium',
    timeStyle: 'short'
  });

  return {
    value: formatter.format(dateValue)
  };
};

const DetailItem: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div className="rounded-2xl border border-slate-200/80 bg-slate-50 px-4 py-3">
    <p className="text-[11px] font-medium tracking-wide text-slate-400 sm:text-xs">{label}</p>
    <p className="mt-1 text-base font-bold leading-6 text-slate-800 sm:text-[1.05rem]">{value}</p>
  </div>
);

const HousekeepingOrderCardCompact: React.FC<HousekeepingOrderCardCompactProps> = ({
  unitName,
  projectName,
  unitTypeGroup,
  unitShortName,
  orderDate,
  createdAt,
  updatedAt,
  creationMode = 'auto',
  status = 'new',
  assigneeName,
  canManageAssignments,
  onPrimaryAction,
  onAssignSelf,
  onOpenAssignModal,
  onToggle,
  isExpanded
}) => {
  const statusDetails = statusConfig[status];
  const orderDateDetails = formatHousekeepingDate(orderDate, creationMode);
  const createdAtDetails = formatHousekeepingDate(createdAt, creationMode);
  const updatedAtDetails = formatHousekeepingDate(updatedAt, creationMode);
  const canAssignSelf = status === 'new' && onAssignSelf;
  const canShowPrimaryAction = status !== 'done';
  const canShowAssignTeamButton = status !== 'done' && canManageAssignments;

  return (
    <div
      onClick={onToggle}
      className={`cursor-pointer overflow-hidden rounded-2xl border ${statusDetails.border} bg-white shadow-sm ${statusDetails.cardGlow} transition hover:-translate-y-0.5 hover:shadow-md`}
      dir="rtl"
      lang="ar"
    >
      <div className={`h-1.5 w-full ${statusDetails.accent}`} />

      <div className="flex flex-col gap-4 p-4 sm:gap-5 md:p-5">
        <div className="min-w-0">
          <span className="min-w-0 text-[1.35rem] font-extrabold leading-8 tracking-tight text-slate-900 sm:text-[1.65rem]">
            {unitName}
          </span>
          <div className=" py-4 flex flex-wrap items-start gap-2.5 sm:items-center sm:gap-3">
            <span
              className={`inline-flex max-w-full rounded-2xl border px-3 py-1.5 text-sm font-semibold leading-5 shadow-sm sm:text-base ${statusDetails.soft} ${statusDetails.border} ${statusDetails.accentText}`}
            >
              {projectName}
            </span>
            <span className="inline-flex max-w-full items-center gap-2 rounded-2xl border border-slate-200 bg-slate-50 px-3 py-1.5 text-xs leading-5 text-slate-700 shadow-sm sm:px-4 sm:text-sm">
              <span className="font-medium text-slate-500">رمز الغرفة</span>
              <span className="h-1 w-1 shrink-0 rounded-full bg-slate-300" />
              <span className="text-sm font-extrabold tracking-wide text-slate-900 sm:text-base">
                {unitShortName || '-'}
              </span>
            </span>

            <span
              className={`inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs font-bold leading-5 shadow-sm sm:text-sm ${statusDetails.soft} ${statusDetails.border} ${statusDetails.text}`}
            >
              <span className={`h-2.5 w-2.5 shrink-0 rounded-full ${statusDetails.accent}`} />
              {statusDetails.label}
            </span>
          </div>

          <div className="flex flex-row justify-between items-center gap-2">
            <div className="text-sm md:text-base font-medium leading-5 text-slate-500 ">
              {orderDateDetails.value}
            </div>
            {assigneeName && (
              <div className="text-xs font-medium text-slate-500 text-left">
                <span className="text-slate-400">المكلف: </span>
                {assigneeName}
              </div>
            )}
          </div>
        </div>

        {(canShowPrimaryAction || canAssignSelf || canShowAssignTeamButton) && (
          <div className="flex w-full flex-col gap-3 border-t border-slate-100 pt-4 md:flex-row md:items-center md:justify-center">
            {canShowPrimaryAction && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onPrimaryAction?.();
                }}
                className="w-full rounded-2xl bg-primary-500 px-4 py-3 text-sm font-bold text-white transition hover:bg-primary-800 sm:text-base md:min-w-[10rem] md:w-auto"
              >
                {statusDetails.primaryAction}
              </button>
            )}

            {/* {canAssignSelf && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onAssignSelf?.();
                }}
                className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm font-bold text-slate-700 transition hover:bg-slate-100 sm:text-base md:min-w-[10rem] md:w-auto"
              >
                تعيين المهمة لي
              </button>
            )} */}

            {canShowAssignTeamButton && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onOpenAssignModal?.();
                }}
                className="w-full rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm font-bold text-emerald-700 transition hover:bg-emerald-100 sm:text-base md:min-w-[10rem] md:w-auto"
              >
                تعيين فريق النظافة
              </button>
            )}
          </div>
        )}
      </div>

      {isExpanded && (
        <div className="border-t border-slate-100 p-4 md:p-5 space-y-4">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <DetailItem label="نوع الغرفة" value={unitTypeGroup || '-'} />
            <DetailItem label="تاريخ الطلب" value={orderDateDetails.value} />
            <DetailItem label="تاريخ الإنشاء" value={createdAtDetails.value} />
            <DetailItem label="آخر تحديث" value={updatedAtDetails.value} />
          </div>

          <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
            <p className="text-sm font-bold text-slate-800">موظف النظافة المكلف</p>
            <div className="mt-3 flex flex-wrap gap-2">
              {assigneeName ? (
                <span className="inline-flex items-center rounded-full bg-white px-3 py-1 text-xs font-semibold text-slate-700 border border-slate-200">
                  {assigneeName}
                </span>
              ) : (
                <span className="text-xs text-slate-400">لا يوجد موظف نظافة معين حالياً</span>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HousekeepingOrderCardCompact;
